import { useState, useEffect, useRef } from 'react';

interface PositionOptions {
    enabled?: boolean;
    offset: { top: number; left: number };
}

export function useElementPosition(
    elementRef: React.RefObject<HTMLElement | null>,
    options: PositionOptions = { offset: { top: 0, left: 0 } }
) {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const resizeTimeout = useRef<number | null>(null);
    const intervalRef = useRef<number | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);

    const enabled = options.enabled ?? true;
    const offsetTop = options.offset.top;
    const offsetLeft = options.offset.left;

    const updatePosition = () => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom - rect.height + offsetTop,
                left: rect.left + rect.width - offsetLeft,
            });
        }
    };

    const handleResize = () => {
        updatePosition();

        // Clear any previous interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Continuously update every 50ms while resizing
        intervalRef.current = window.setInterval(updatePosition, 50);

        // Stop updating after 200ms of no resizing
        if (resizeTimeout.current) {
            clearTimeout(resizeTimeout.current);
        }

        resizeTimeout.current = window.setTimeout(() => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }, 200);
    };

    useEffect(() => {
        if (enabled) {
            updatePosition();

            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleResize);
            observerRef.current = new MutationObserver(() => {
                requestAnimationFrame(handleResize);
            });
            observerRef.current.observe(document.body, {
                attributes: true,
                childList: true,
                subtree: true,
            });

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleResize);
                if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
                if (intervalRef.current) clearInterval(intervalRef.current);
                if (observerRef.current) observerRef.current.disconnect();
            };
        }
    }, [enabled]);

    return position;
}
